import { useRef } from 'react'
import { Link } from '@tanstack/react-router'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { LoadingButton } from '@/components/loading-button'
import { ImageUp, Trash2 } from 'lucide-react'
import { useStaffMember, useStaffPhoto, useStaffProjects, useUpdateStaffMember } from '@/api/hooks'
import { StaffAvatar } from '@/components/staff-avatar'
import { BackButton } from '@/components/back-button'
import { ChoicePicker } from '@/components/choice-picker'
import { POInlineEditableField } from '@/components/po-inline-edit'
import { formatStatus, parseUTCDate } from '@/lib/utils'
import { toast } from 'sonner'
import { usePageTitle } from '@/lib/page-title'
import { getApiErrorMessage } from '@/lib/api-error'

// Data works the manufacturer data agent's items (USA-1474); Finance approves invoices of $10,000 or more (USA-1472).
const ROLE_OPTIONS = ['Project Manager', 'Sales', 'Admin', 'Operations', 'Data', 'Finance']
const ROLE_CHOICES = ROLE_OPTIONS.map((role) => ({ value: role, label: role }))

const ROLE_COLORS: Record<string, 'default' | 'secondary' | 'outline'> = {
  'Project Manager': 'default',
  Admin: 'secondary',
  Sales: 'outline',
  Operations: 'outline',
  Data: 'outline',
  Finance: 'secondary',
}

interface StaffDetailPageProps {
  staffId: string
}

export function StaffDetailPage({ staffId }: StaffDetailPageProps) {
  const { data: user, isLoading } = useStaffMember(staffId)
  usePageTitle(user?.name)
  const { data: projects, isLoading: projectsLoading } = useStaffProjects(staffId)
  const updateStaff = useUpdateStaffMember(staffId)
  const staffPhoto = useStaffPhoto(staffId)
  const photoInput = useRef<HTMLInputElement>(null)

  if (isLoading) {
    return (
      <div className="mx-auto max-w-7xl space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  if (!user) {
    return (
      <div className="mx-auto max-w-7xl space-y-6">
        <p className="text-muted-foreground">Staff member not found.</p>
      </div>
    )
  }

  const handleRoleSave = (selectedRole: string) => {
    updateStaff.mutate(
      { role: selectedRole || undefined },
      {
        onSuccess: () => toast.success('Role updated'),
        onError: () => toast.error('Failed to update role'),
      },
    )
  }

  const savePhoto = (file: File | null) => {
    staffPhoto.mutate(file, {
      onSuccess: () => toast.success(file ? 'Photo updated' : 'Photo removed'),
      onError: (error) => toast.error(getApiErrorMessage(error, 'Failed to update photo')),
    })
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <BackButton fallback={{ to: '/staff' }} fallbackLabel="Back to Staff" />
      </div>
      <div className="flex items-center gap-3">
        <StaffAvatar
          name={user.name}
          photoUrl={user.photo_url}
          className="size-14"
          fallbackClassName="text-lg font-semibold"
        />
        <div className="flex flex-1 flex-wrap items-center gap-3">
          <h1 className="text-2xl font-bold tracking-tight">{user.name}</h1>
          <ChoicePicker
            title="Role"
            label={`Role: ${user.role ?? 'none'}. Change role`}
            value={user.role ?? ''}
            choices={ROLE_CHOICES}
            pending={updateStaff.isPending}
            onSelect={handleRoleSave}
          >
            {user.role ? (
              <Badge variant={ROLE_COLORS[user.role] ?? 'outline'}>{user.role}</Badge>
            ) : (
              <span className="px-1 text-sm text-muted-foreground">No role</span>
            )}
          </ChoicePicker>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Details</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-muted-foreground">Title</dt>
              <dd>
                {/* Shown beside their name when they take over a store chat (USA-1538). */}
                <POInlineEditableField
                  value={user.title ?? ''}
                  placeholder="Add title"
                  ariaLabel="Title"
                  showEditIcon
                  pending={updateStaff.isPending}
                  onConfirm={(raw) =>
                    updateStaff.mutate(
                      { title: raw.trim() },
                      {
                        onSuccess: () => toast.success('Title updated'),
                        onError: () => toast.error('Failed to update title'),
                      },
                    )
                  }
                />
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Email</dt>
              <dd>{user.email}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Phone</dt>
              <dd>
                <POInlineEditableField
                  value={user.staff_phone?.phone ?? ''}
                  placeholder="Add phone"
                  ariaLabel="Phone"
                  showEditIcon
                  pending={updateStaff.isPending}
                  onConfirm={(raw) =>
                    updateStaff.mutate(
                      { phone: raw.trim() },
                      {
                        onSuccess: () => toast.success('Phone updated'),
                        onError: () => toast.error('Failed to update phone'),
                      },
                    )
                  }
                />
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Photo</dt>
              <dd className="flex flex-wrap items-center gap-1">
                <input
                  ref={photoInput}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="sr-only"
                  tabIndex={-1}
                  aria-hidden
                  onChange={(event) => {
                    const file = event.target.files?.[0]
                    event.target.value = ''
                    if (file) savePhoto(file)
                  }}
                />
                <LoadingButton
                  variant="outline"
                  size="sm"
                  loading={staffPhoto.isPending && staffPhoto.variables !== null}
                  loadingText="Uploading..."
                  disabled={staffPhoto.isPending}
                  onClick={() => photoInput.current?.click()}
                >
                  <ImageUp /> {user.photo_url ? 'Change photo' : 'Upload photo'}
                </LoadingButton>
                {user.photo_url ? (
                  <LoadingButton
                    variant="ghost"
                    size="sm"
                    loading={staffPhoto.isPending && staffPhoto.variables === null}
                    loadingText="Removing..."
                    disabled={staffPhoto.isPending}
                    onClick={() => savePhoto(null)}
                  >
                    <Trash2 /> Remove
                  </LoadingButton>
                ) : null}
              </dd>
            </div>
            <div>
              <dt id="store-chats-label" className="text-muted-foreground">
                Answers store chats
              </dt>
              <dd className="pt-1">
                {/* Store chat responders get chat phone alerts, the chime and the Chats badge (USA-1483). */}
                <Switch
                  aria-labelledby="store-chats-label"
                  checked={!!user.store_chat_responder}
                  disabled={updateStaff.isPending}
                  onCheckedChange={(checked) =>
                    updateStaff.mutate(
                      { store_chat_responder: checked },
                      {
                        onSuccess: () => toast.success(checked ? 'Now answers store chats' : 'No longer answers store chats'),
                        onError: () => toast.error('Failed to update store chats'),
                      },
                    )
                  }
                />
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Member Since</dt>
              <dd>{parseUTCDate(user.created_at).toLocaleDateString()}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      <div>
        <h2 className="mb-3 text-lg font-semibold">Managed Projects</h2>
        {/* Desktop table */}
        <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Project</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {projectsLoading ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : projects?.length ? (
                projects.map((project) => (
                  <TableRow key={project.id}>
                    <TableCell>
                      <Link
                        to="/projects/$projectId"
                        params={{ projectId: project.uuid }}
                        className="font-medium text-primary hover:underline"
                      >
                        {project.name}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{formatStatus(project.status)}</Badge>
                    </TableCell>
                    <TableCell>
                      {project.customer_group ? (
                        <Link
                          to="/customers/$groupId"
                          params={{ groupId: String(project.customer_group.uuid) }}
                          className="text-primary hover:underline"
                        >
                          {project.customer_group.name}
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {parseUTCDate(project.created_at).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground">
                    No managed projects
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Mobile cards */}
        <div className="space-y-2 md:hidden">
          {projectsLoading ? (
            <div className="rounded-lg border bg-card py-4 text-center text-sm text-muted-foreground">
              Loading...
            </div>
          ) : projects?.length ? (
            projects.map((project) => (
              <div key={project.id} className="rounded-lg border bg-card p-3">
                <div className="flex items-start justify-between gap-2">
                  <Link
                    to="/projects/$projectId"
                    params={{ projectId: project.uuid }}
                    className="min-w-0 flex-1 break-words text-sm font-medium text-primary hover:underline"
                  >
                    {project.name}
                  </Link>
                  <Badge variant="secondary" className="shrink-0">
                    {formatStatus(project.status)}
                  </Badge>
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                  {project.customer_group ? (
                    <Link
                      to="/customers/$groupId"
                      params={{ groupId: String(project.customer_group.uuid) }}
                      className="break-words text-primary hover:underline"
                    >
                      {project.customer_group.name}
                    </Link>
                  ) : null}
                  <span>Created {parseUTCDate(project.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))
          ) : (
            <div className="rounded-lg border bg-card py-4 text-center text-sm text-muted-foreground">
              No managed projects
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
